import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { parseOpenTarget } from '@/lib/openTarget';
import { cn } from '@/lib/utils';

interface OpenAnythingProps {
  /** Called after a successful navigation (e.g. to close a dialog). */
  onNavigate?: () => void;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Paste-anything opener: NIP-19 identifiers, nostr: URIs, links from other
 * Nostr clients, bare hex ids, or plain web URLs.
 */
export function OpenAnything({ onNavigate, autoFocus, className }: OpenAnythingProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const target = parseOpenTarget(value);
    if (!target) {
      setError(true);
      return;
    }
    setValue('');
    setError(false);
    onNavigate?.();
    navigate(target.path);
  };

  return (
    <form onSubmit={submit} className={cn('space-y-2', className)}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            placeholder="Paste an naddr, note, npub, nostr: link, or URL…"
            className="pl-9"
            autoFocus={autoFocus}
            aria-label="Open a Nostr identifier or URL"
            aria-invalid={error}
          />
        </div>
        <Button type="submit" variant="secondary">
          Open
        </Button>
      </div>
      {error && (
        <p className="text-xs text-destructive">
          Couldn't recognize that. Try an naddr1…, note1…, npub1…, a nostr: link, a link from
          another Nostr client, or a web URL.
        </p>
      )}
    </form>
  );
}
